'use client';

import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBrowseFilters } from '@/lib/hooks/useBrowseFilters';
import { getGoalMetadata } from '@/lib/aviado/goals';
import { formatSupplementName, getArticleKindInfo } from '@/lib/articles';

/**
 * ActiveFilterChips — pill chips for currently-applied filters with × remove
 * buttons, plus a "Clear all" shortcut.
 *
 * Spec: browse.html `.active-summary` + `.active-tag`.
 */

const TIER_LABEL: Record<string, string> = {
  strong: 'Strong',
  moderate: 'Moderate',
  preliminary: 'Preliminary',
  insufficient: 'Insufficient',
};

const TIME_LABEL: Record<string, string> = {
  '<5': '< 5 min',
  '5-15': '5–15 min',
  '15+': '15+ min',
};

export function ActiveFilterChips() {
  const { filters, set, clear } = useBrowseFilters();

  const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];

  if (filters.tier) {
    chips.push({
      key: 'tier',
      label: TIER_LABEL[filters.tier] ?? filters.tier,
      onRemove: () => set({ tier: null }),
    });
  }
  if (filters.goal) {
    const meta = getGoalMetadata(filters.goal);
    chips.push({
      key: 'goal',
      label: meta?.label ?? filters.goal,
      onRemove: () => set({ goal: null }),
    });
  }
  if (filters.kind) {
    chips.push({
      key: 'kind',
      label: getArticleKindInfo(filters.kind).label,
      onRemove: () => set({ kind: null }),
    });
  }
  if (filters.time) {
    chips.push({
      key: 'time',
      label: TIME_LABEL[filters.time] ?? filters.time,
      onRemove: () => set({ time: null }),
    });
  }
  if (filters.supplement) {
    chips.push({
      key: 'supplement',
      label: formatSupplementName(filters.supplement),
      onRemove: () => set({ supplement: null }),
    });
  }
  if (filters.topic) {
    chips.push({
      key: 'topic',
      label: `Topic: ${filters.topic}`,
      onRemove: () => set({ topic: null }),
    });
  }
  if (filters.q) {
    chips.push({
      key: 'q',
      label: `“${filters.q}”`,
      onRemove: () => set({ q: '' }),
    });
  }

  if (chips.length === 0) {
    return (
      <div className="max-w-container mx-auto px-10 mt-4 font-mono text-[11px] tracking-[0.04em] text-slate min-h-[28px]">
        No filters applied — showing all results.
      </div>
    );
  }

  return (
    <div className="max-w-container mx-auto px-10 mt-4">
      <div className="flex gap-2 items-center flex-wrap min-h-[28px]">
        {chips.map((chip) => (
          <span
            key={chip.key}
            className={cn(
              'inline-flex items-center gap-1.5',
              'px-2.5 py-1 rounded-pill',
              'bg-white border border-border',
              'font-ui text-[11.5px] text-ink'
            )}
          >
            {chip.label}
            <button
              type="button"
              onClick={chip.onRemove}
              aria-label={`Remove filter: ${chip.label}`}
              className="p-[2px] bg-transparent border-0 text-slate hover:text-ink cursor-pointer transition-colors duration-fast"
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={clear}
          className="font-mono text-[11px] tracking-[0.04em] text-aviado-blue hover:text-warm-navy cursor-pointer bg-transparent border-0 ml-1 transition-colors duration-fast"
        >
          Clear all
        </button>
      </div>
    </div>
  );
}

export default ActiveFilterChips;
